/**
 * Section: videos-carousel V2
 * Le Sensier — Carrossel coverflow de videos verticais 9:16 estilo Stories.
 *
 * V2 changes:
 * - Base class: ScrollCarousel -> EffectCarousel (loop infinito nativo via modulo em theme.js:825-829)
 * - Layout: scroll-snap horizontal -> coverflow absoluto (CSS variable --vc-offset)
 * - Drag/swipe: implementado manualmente (EffectCarousel allow-swipe usa GestureArea, mas queremos
 *   feedback visual durante drag, entao implementamos pointerdown/move/up custom)
 * - Click no video-trigger (button no media) -> dispatch 'videos-fullscreen:open'
 * - Click no product-card (<a>) -> segue href naturalmente (PDP)
 *
 * Eventos do EffectCarousel reaproveitados:
 * - carousel:change -> _updatePositions (anima slides via CSS)
 * - carousel:settle -> _updatePositions + _syncPlayback (toca/pausa videos)
 */

(function () {
  'use strict';

  const EffectCarouselCtor = window.customElements?.get?.('effect-carousel');

  if (!EffectCarouselCtor) {
    console.warn('[videos-carousel] EffectCarousel base nao encontrado. Carrossel rodara em fallback statico.');
    return;
  }

  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;

  class VideosCarousel extends EffectCarouselCtor {
    connectedCallback() {
      super.connectedCallback?.();

      this._onChange = this._onChange.bind(this);
      this._onSettle = this._onSettle.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);

      this.addEventListener('carousel:change', this._onChange);
      this.addEventListener('carousel:settle', this._onSettle);

      this.addEventListener('click', this._onClick);
      this.addEventListener('pointerdown', this._onPointerDown);

      this._dragState = { active: false, startX: 0, startY: 0, deltaX: 0, locked: null, pointerId: null };
      this._initialized = false;

      // V11 fix #3 (FOUC): posicionar slides + revelar a section imediatamente (so layout, sem tocar video).
      requestAnimationFrame(() => {
        this._updatePositions();
        this.classList.add('is-ready');
      });

      // V11 fix #4 (perf): lazy-init do PLAYBACK — so baixa/toca video quando a section entra no viewport.
      // Antes disso, todos os <video> ficam preload="none" (definido no Liquid). Banners do topo carregam livres.
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting) && !this._initialized) {
            this._initialized = true;
            this._io.disconnect();
            this._io = null;
            this._preloadNeighbors();
            this._syncPlayback({ initial: true });
          }
        }, { rootMargin: '300px' });
        this._io.observe(this);
        // Sem safety-net timeout: se a section nunca entra no viewport, os videos NUNCA carregam
        // (exatamente o desejado — economia total de banda). O fallback `else` cobre o caso raro
        // de browser sem IntersectionObserver.
      } else {
        // Fallback browsers sem IntersectionObserver: inicializa direto
        this._initialized = true;
        requestAnimationFrame(() => {
          this._preloadNeighbors();
          this._syncPlayback({ initial: true });
        });
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback?.();
      this.removeEventListener('carousel:change', this._onChange);
      this.removeEventListener('carousel:settle', this._onSettle);
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('pointerdown', this._onPointerDown);
      this._io?.disconnect();
      this._pauseAll();
    }

    // Sobrescreve animation do EffectCarousel (fade default). Coverflow e via CSS variable --vc-offset,
    // entao a animation "oficial" e noop.
    createOnChangeAnimationControls(fromSlide, toSlide) {
      // Retorna objeto compatible com a API esperada por select()
      const noop = { finished: Promise.resolve(), finish: () => {} };
      return noop;
    }

    createOnBecameVisibleAnimationControls(toSlide) {
      return { finished: Promise.resolve(), finish: () => {} };
    }

    _onChange() {
      this._updatePositions();
      // V13: sincronizar o icone do novo centro AGORA (no change), nao no settle —
      // no settle o toggle ja esta visivel e a troca de icone vira piscada.
      try {
        const cells = this.cells || [];
        const idx = this.selectedIndex ?? 0;
        const cell = cells[idx];
        const video = cell && cell.querySelector('video');
        if (cell && video) this._syncMuteToggleUI(cell, video.muted);
      } catch (e) {}
      // Pre-buffer central + immediate neighbors
      this._preloadNeighbors();
    }

    _onSettle() {
      this._updatePositions();
      this._syncPlayback();
    }

    _updatePositions() {
      const cells = this.cells || [];
      const total = cells.length;
      if (total === 0) return;
      const selectedIdx = this.selectedIndex ?? 0;

      cells.forEach((cell, i) => {
        let delta = i - selectedIdx;
        // wraparound cyclic — sempre escolher caminho mais curto pelo anel
        if (delta > total / 2) delta -= total;
        else if (delta < -total / 2) delta += total;

        cell.style.setProperty('--vc-offset', delta);

        // V8: 5 niveis (center / neighbor / far / very-far / hidden) = 7 cards visiveis em desktop
        const absDelta = Math.abs(delta);
        let position;
        if (delta === 0) position = 'center';
        else if (absDelta === 1) position = 'neighbor';
        else if (absDelta === 2) position = 'far';
        else if (absDelta === 3) position = 'very-far';
        else position = 'hidden';
        cell.dataset.position = position;

        // V8: data-side controla sinal do translate via CSS (--vc-side: -1 esq, 1 dir, 0 center)
        cell.dataset.side = delta < 0 ? 'left' : (delta > 0 ? 'right' : 'center');

        cell.classList.toggle('is-selected', delta === 0);
      });
    }

    _preloadNeighbors() {
      const cells = this.cells || [];
      const total = cells.length;
      if (total === 0) return;
      const selectedIdx = this.selectedIndex ?? 0;

      // V7: preload tier 3-niveis pra reduzir travamento por buffer underrun
      // CENTER: auto (carrega total) | VIZINHO direto: metadata (5%) | FAR/HIDDEN: none (zero)
      // So 1 video por vez baixa stream completo — alivia decoder + banda.
      cells.forEach((cell, i) => {
        const video = cell.querySelector('video');
        if (!video) return;

        let delta = i - selectedIdx;
        if (delta > total / 2) delta -= total;
        else if (delta < -total / 2) delta += total;

        const absDelta = Math.abs(delta);
        const targetPreload =
          absDelta === 0 ? 'auto' :
          absDelta === 1 ? 'metadata' :
          'none';

        if (video.preload !== targetPreload) {
          video.preload = targetPreload;
        }
      });
    }

    _syncPlayback(opts = {}) {
      const cells = this.cells || [];
      const total = cells.length;
      if (total === 0) return;
      const selectedIdx = this.selectedIndex ?? 0;

      cells.forEach((cell, i) => {
        const video = cell.querySelector('video');
        const isCenter = i === selectedIdx;

        if (prefersReducedMotion) {
          cell.setAttribute('data-reduced-motion', '');
          if (video) {
            try { video.pause(); } catch (e) {}
          }
          return;
        }

        if (!video) return;

        if (isCenter) {
          // V11 fix #2 (pisca preto): voltar ao `loop` NATIVO do HTML5. O seek manual (ended+rAF)
          // tinha o MESMO gap de decoder. Com o poster como background do <video> (CSS), o brief
          // gap do loop mostra o poster (claro) em vez de preto. Loop nativo = sem reset extra.
          if (!video.hasAttribute('loop')) {
            video.setAttribute('loop', '');
          }
          // Remove handler manual legado se existir (idempotencia ao re-entrar no center)
          if (video._vcLoopHandler) {
            video.removeEventListener('ended', video._vcLoopHandler);
            video._vcLoopHandler = null;
          }

          if (!opts.initial && video.readyState >= 1) {
            try { video.currentTime = 0; } catch (e) {}
          }

          const playPromise = video.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise
              .then(() => {
                cell.removeAttribute('data-suspended');
              })
              .catch((err) => {
                if (err && err.name === 'NotAllowedError') {
                  cell.setAttribute('data-suspended', '');
                } else if (window.Shopify?.designMode) {
                  console.debug('[videos-carousel] play() falhou:', err);
                }
              });
          }

          // Sincroniza UI do mute toggle com o estado real (so o center tem toggle visivel)
          this._syncMuteToggleUI(cell, video.muted);
        } else {
          try {
            video.pause();
            // V7: reset currentTime libera buffer/CPU dos vizinhos
            if (video.currentTime > 0.1) video.currentTime = 0;
          } catch (e) {}

          // Cleanup do loop handler legado ao perder centro
          if (video._vcLoopHandler) {
            video.removeEventListener('ended', video._vcLoopHandler);
            video._vcLoopHandler = null;
          }

          // V11 fix #1 (audio pisca): resetar muted SEM tocar no UI do toggle.
          // O toggle do slide saindo esta em fade-out (opacity>0); trocar icone agora causa "piscada".
          // O toggle so e visivel/relevante no center, entao nao mexemos no UI aqui.
          if (video.muted === false) {
            video.muted = true;
          }
        }
      });
    }

    _syncMuteToggleUI(cell, isMuted) {
      const toggle = cell.querySelector('.videos-carousel__play-toggle');
      if (!toggle) return;
      // V13: sem rewrite quando o estado é o mesmo — trocar ícone à toa = piscada.
      if (toggle.dataset.vcMuted === String(isMuted)) return;
      toggle.dataset.vcMuted = String(isMuted);
      toggle.setAttribute('aria-pressed', String(isMuted));
      toggle.setAttribute('aria-label', isMuted ? 'Ativar som do vídeo' : 'Silenciar vídeo');
      const mutedIcon = toggle.querySelector('.videos-carousel__play-icon--muted');
      const unmutedIcon = toggle.querySelector('.videos-carousel__play-icon--unmuted');
      if (mutedIcon) mutedIcon.hidden = !isMuted;
      if (unmutedIcon) unmutedIcon.hidden = isMuted;
    }

    _pauseAll() {
      (this.cells || []).forEach((cell) => {
        const video = cell.querySelector('video');
        if (video) {
          try { video.pause(); } catch (e) {}
        }
      });
    }

    // CLICK HANDLING — V3: separa play-toggle / product-card / video-trigger
    _onClick(e) {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      // 1. Play/mute toggle — sempre prevalece (z-index alto)
      const toggle = e.target.closest('.videos-carousel__play-toggle');
      if (toggle) {
        e.preventDefault();
        e.stopPropagation();
        this._handleMuteToggle(toggle);
        return;
      }

      // 2. Product card — deixa <a href> fazer o trabalho (NAO interceptar)
      if (e.target.closest('.videos-carousel__product-card')) return;

      // 3. Video trigger
      const trigger = e.target.closest('.videos-carousel__video-trigger');
      if (!trigger) return;

      // Se o drag estava ativo, ignora (era drag, nao click)
      if (this._dragState.consumed) {
        this._dragState.consumed = false;
        return;
      }

      e.preventDefault();
      const slide = trigger.closest('.videos-carousel__slide');
      if (!slide) return;
      const index = parseInt(slide.dataset.slideIndex, 10) || 0;

      // V3 DEFAULT: click no video seleciona o slide (carrossel anima ate ele)
      // V2 LEGACY: se enable_fullscreen=true, dispara open do viewer
      const enableFullscreen = this.dataset.enableFullscreen === 'true';

      if (enableFullscreen) {
        const sectionId = this.dataset.sectionId || this.id;
        document.dispatchEvent(new CustomEvent('videos-fullscreen:open', {
          bubbles: true,
          detail: { sectionId: sectionId, startIndex: index, sourceCarousel: this }
        }));
      } else {
        // Se o slide ja eh o central, click ativa som (toggle mute)
        if (index === this.selectedIndex) {
          const video = slide.querySelector('video');
          if (video) {
            video.muted = !video.muted;
            this._syncMuteToggleUI(slide, video.muted);
          }
          return;
        }
        // Caso contrario, animar carrossel ate o slide clicado
        this.select?.(index);
      }
    }

    _handleMuteToggle(toggleBtn) {
      const slide = toggleBtn.closest('.videos-carousel__slide');
      if (!slide) return;
      const video = slide.querySelector('video');
      if (!video) return;
      // So permite toggle no slide central
      if (slide.dataset.position !== 'center') return;
      video.muted = !video.muted;
      this._syncMuteToggleUI(slide, video.muted);
    }

    // DRAG/SWIPE HORIZONTAL — pointerdown/move/up custom
    _onPointerDown(e) {
      // Nao iniciar drag em clicks em links/buttons explicitos do product-card
      if (e.target.closest('.videos-carousel__product-card')) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      this._dragState.active = true;
      this._dragState.locked = null; // ainda nao decidiu eixo
      this._dragState.startX = e.clientX;
      this._dragState.startY = e.clientY;
      this._dragState.deltaX = 0;
      this._dragState.pointerId = e.pointerId;
      this._dragState.consumed = false;

      this.addEventListener('pointermove', this._onPointerMove);
      this.addEventListener('pointerup', this._onPointerUp);
      this.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(e) {
      if (!this._dragState.active) return;
      if (e.pointerId !== this._dragState.pointerId) return;

      const dx = e.clientX - this._dragState.startX;
      const dy = e.clientY - this._dragState.startY;

      // Lock no eixo dominante apos 6px de movimento
      if (this._dragState.locked === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        this._dragState.locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (this._dragState.locked === 'y') {
          // Movimento vertical: cancelar (usuario scrolla a pagina)
          this._dragState.active = false;
          this.removeEventListener('pointermove', this._onPointerMove);
          this.removeEventListener('pointerup', this._onPointerUp);
          this.removeEventListener('pointercancel', this._onPointerUp);
          return;
        }
        // Lock em X: capturar pointer
        try { this.setPointerCapture(this._dragState.pointerId); } catch (err) {}
      }

      if (this._dragState.locked !== 'x') return;

      this._dragState.deltaX = dx;

      // Visual feedback: aplicar offset temporario.
      // V8: usa --vc-step-1 (distancia center->neighbor) como unidade do drag factor.
      const cellWidth = this._getEffectiveCellWidth();
      const stepFromVar = parseFloat(getComputedStyle(this).getPropertyValue('--vc-step-1'));
      const stepWidth = (stepFromVar && !isNaN(stepFromVar))
        ? stepFromVar
        : (cellWidth + (parseFloat(getComputedStyle(this).getPropertyValue('--vc-gap')) || 16));
      const dragFactor = dx / (stepWidth || 1);
      this.style.setProperty('--vc-drag', dragFactor.toFixed(3));

      // Marca o carrossel como em-drag (CSS desativa transitions)
      this.dataset.dragging = 'true';

      // V4: threshold 8px (era 4) + so marcar consumed se realmente em drag horizontal.
      // Evita bloquear click subsequente por jitter de mouse.
      if (Math.abs(dx) > 8 && this._dragState.locked === 'x') {
        this._dragState.consumed = true; // bloqueia click subsequente
      }

      // Cancela seleçao de texto durante drag
      if (e.cancelable) e.preventDefault();
    }

    _onPointerUp(e) {
      if (!this._dragState.active && this._dragState.locked !== 'x') {
        this._cleanupDrag();
        return;
      }

      const dx = this._dragState.deltaX;
      const cellWidth = this._getEffectiveCellWidth();
      const threshold = cellWidth * 0.18; // ~18% do card pra trigger navegacao

      this.style.removeProperty('--vc-drag');
      delete this.dataset.dragging;

      if (Math.abs(dx) > threshold) {
        if (dx < 0) this.next?.();
        else this.previous?.();
      }

      this._cleanupDrag();
    }

    _cleanupDrag() {
      try { if (this._dragState.pointerId != null) this.releasePointerCapture(this._dragState.pointerId); } catch (err) {}
      this.removeEventListener('pointermove', this._onPointerMove);
      this.removeEventListener('pointerup', this._onPointerUp);
      this.removeEventListener('pointercancel', this._onPointerUp);

      this._dragState.active = false;
      this._dragState.locked = null;
      this._dragState.deltaX = 0;
      this._dragState.pointerId = null;

      // V4: consumed deve ser resetado por _onClick (que bloqueia 1 click apos drag).
      // Mas se o click event nao chegou (ex: mouseup fora do elemento), agendamos reset
      // defensivo no proximo tick — garante que clicks futuros nao fiquem bloqueados.
      if (this._dragState.consumed) {
        setTimeout(() => { this._dragState.consumed = false; }, 50);
      }
    }

    _getEffectiveCellWidth() {
      const first = this.cells?.[0];
      if (!first) return 320;
      const rect = first.getBoundingClientRect();
      return rect.width || 320;
    }
  }

  if (!window.customElements.get('videos-carousel')) {
    window.customElements.define('videos-carousel', VideosCarousel);
  }
})();
